import { ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useMemo, useRef } from "react";
import bigDecimal from "js-big-decimal";
import { AssetProps } from "./types";
import Translations from "@translate/components/Portfolio/AssetInsight/DistributionCard/Asset";
import { I18nUtil } from "@utils/i18n";
import { usePieChart } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

const configColor = [
  {
    color: ospThemes.colors.content_brand_primary,
    hiddenColor: ospThemes.colors.content_brand_tertiary,
  },
  {
    color: ospThemes.colors.content_accent4_primary,
    hiddenColor: ospThemes.colors.content_accent4_tertiary,
  },
  {
    color: ospThemes.colors.content_accent3_primary,
    hiddenColor: ospThemes.colors.content_accent3_tertiary,
  },
  {
    color: ospThemes.colors.content_accent5_primary,
    hiddenColor: ospThemes.colors.content_accent5_tertiary,
  },
  {
    color: ospThemes.colors.background_auralv3_overlay100,
    hiddenColor: ospThemes.colors.background_auralv3_overlay40,
  },
  {
    color: ospThemes.colors.background_white_overlay40,
    hiddenColor: ospThemes.colors.background_white_overlay20,
  },
];

export const Asset = ({ onViewall, className, topAssets }: AssetProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const data = useMemo(() => {
    if (!topAssets || !topAssets.length) {
      return [];
    }
    let totalPercentage = "0";
    const top6Data = topAssets.slice(0, 5).map((item, index) => {
      const value = bigDecimal.round(
        item.value,
        2,
        bigDecimal.RoundingModes.DOWN,
      );
      totalPercentage = bigDecimal.add(totalPercentage, value);
      return {
        key: item.key,
        label: item.label,
        value: Number(value),
        ...configColor[index],
      };
    });
    if (topAssets.length < 6 && Number(totalPercentage) !== 100) {
      const diff = bigDecimal.subtract(totalPercentage, "100");
      const latest = new bigDecimal(top6Data.at(-1)!.value).getValue();
      top6Data.at(-1)!.value = Number(bigDecimal.subtract(latest, diff));
    }
    const latestValue = Number(bigDecimal.subtract("100", totalPercentage));
    if (topAssets.length === 6) {
      top6Data.push({
        key: topAssets[5].key,
        label: topAssets[5].label,
        value: latestValue,
        ...configColor[5],
      });
    }
    if (topAssets.length > 6) {
      top6Data.push({
        key: "others",
        label: I18n.t("Others"),
        value: latestValue,
        ...configColor[5],
      });
    }
    return top6Data.filter((item) => item.value > 0);
  }, [topAssets]);
  const { setHoverKey: setChartHoverKey } = usePieChart(chartRef, {
    data,
    centerContent: {
      value: topAssets?.length || 0,
      label: I18n.t("Total"),
    },
    tooltipFormatter: (item) => `
      <div class="px-1.5 py-1 flex gap-0.5 bg-background_solid_quaternary rounded-1 items-center text-content_primary">
        <div class="body_s">${item.label}</div>
        <div class="body_medium_s">${item.value}%</div>
      </div>
    `,
  });
  // 图例
  const legendData = useMemo(() => {
    return data.filter((item) => item.key !== "others");
  }, [data]);
  return (
    <div className={cn("flex flex-col md:flex-row justify-between", className)}>
      <div className="w-80.25 h-52 md:h-46.5 md:w-61 flex items-center justify-center">
        <div className="w-48 h-48 md:w-39 md:h-39" ref={chartRef} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-x-[3px] auto-rows-max self-end items-end w-full md:w-42">
        {legendData.map((item) => (
          <div
            key={item.key}
            className="grid grid-cols-2 gap-x-3 py-1 px-3 md:hover:bg-background_white_overlay10 rounded-2 items-center"
            onMouseEnter={() => {
              // 鼠标进入图例时高亮对应扇区
              setChartHoverKey(item.key);
            }}
            onMouseLeave={() => {
              // 鼠标离开图例时恢复
              setChartHoverKey(null);
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full flex shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="body_m text-content_primary truncate">
                {item.label}
              </span>
            </div>
            <span className="body_medium_m text-content_primary">
              {item.value}%
            </span>
          </div>
        ))}
        <div
          className={cn(
            "hidden grid-cols-2 gap-x-3 py-1 px-3 md:hover:bg-background_white_overlay10 rounded-2 items-center",
            { grid: data.at(-1)?.key === "others" },
          )}
          onMouseEnter={() => {
            // 鼠标进入图例时高亮对应扇区
            setChartHoverKey("others");
          }}
          onMouseLeave={() => {
            // 鼠标离开图例时恢复
            setChartHoverKey(null);
          }}
        >
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex shrink-0"
              style={{ backgroundColor: data.at(-1)?.color }}
            />
            <span className="body_m text-content_primary">
              {data.at(-1)?.label}
            </span>
          </div>
          <span
            data-openreplay-label={`DistributionCard · Asset · span | ${I18n.t("View all")}`}
            className="body_medium_m text-content_accent4_primary cursor-pointer"
            onClick={onViewall}
          >
            {I18n.t("View all")}
          </span>
        </div>
      </div>
    </div>
  );
};
